import { Globe, DollarSign } from "lucide-react";
import { useRegion, Region, Currency } from "@/contexts/RegionContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const regions: { value: Region; label: string; flag: string; description: string }[] = [
  { value: "europe", label: "Europe", flag: "🇪🇺", description: "Metric (m²)" },
  { value: "international", label: "International", flag: "🌍", description: "Imperial (sq ft)" },
];

const currencies: { value: Currency; label: string; symbol: string }[] = [
  { value: "EUR", label: "Euro", symbol: "€" },
  { value: "USD", label: "US Dollar", symbol: "$" },
  { value: "GBP", label: "British Pound", symbol: "£" },
  { value: "AED", label: "UAE Dirham", symbol: "د.إ" },
];

export const RegionSelector = ({ variant = "desktop" }: { variant?: "desktop" | "mobile" }) => {
  const { region, setRegion, currency, setCurrency } = useRegion();
  const currentRegion = regions.find((r) => r.value === region);
  const currentCurrency = currencies.find((c) => c.value === currency);

  if (variant === "mobile") {
    return (
      <div className="space-y-4 pt-4 border-t border-border/30">
        <div>
          <p className="font-body text-xs uppercase tracking-wider text-muted-foreground mb-3">Region</p>
          <div className="flex gap-2">
            {regions.map((r) => (
              <button
                key={r.value}
                onClick={() => setRegion(r.value)}
                className={`flex-1 px-4 py-3 rounded-lg border transition-colors ${
                  region === r.value
                    ? "bg-gold/10 border-gold text-gold"
                    : "border-border/50 text-foreground/70 hover:border-gold/50"
                }`}
              >
                <span className="text-lg mr-2">{r.flag}</span>
                <span className="font-body text-sm">{r.label}</span>
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="font-body text-xs uppercase tracking-wider text-muted-foreground mb-3">Currency</p>
          <div className="grid grid-cols-2 gap-2">
            {currencies.map((c) => (
              <button
                key={c.value}
                onClick={() => setCurrency(c.value)}
                className={`px-4 py-3 rounded-lg border transition-colors ${
                  currency === c.value
                    ? "bg-gold/10 border-gold text-gold"
                    : "border-border/50 text-foreground/70 hover:border-gold/50"
                }`}
              >
                <span className="font-body text-sm">{c.symbol} {c.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-2 px-3 py-2 text-sm text-cream/70 hover:text-cream transition-colors duration-300 focus:outline-none"
          aria-label="Select region and currency"
        >
          <Globe size={16} />
          <span className="hidden sm:inline">{currentRegion?.flag}</span>
          <span className="hidden sm:inline text-xs text-cream/50">|</span>
          <span className="hidden sm:inline text-xs">{currentCurrency?.symbol}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-56 bg-charcoal border border-cream/20 shadow-luxury z-50"
      >
        <DropdownMenuLabel className="text-cream/50 text-xs uppercase tracking-wider px-4 py-2">
          Region & Units
        </DropdownMenuLabel>
        {regions.map((r) => (
          <DropdownMenuItem
            key={r.value}
            onClick={() => setRegion(r.value)}
            className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
              region === r.value 
                ? "bg-gold/10 text-gold" 
                : "text-cream/80 hover:bg-cream/5 hover:text-cream"
            }`}
          >
            <span className="text-lg">{r.flag}</span>
            <div className="flex flex-col">
              <span className="font-body text-sm">{r.label}</span>
              <span className="font-body text-xs text-cream/50">{r.description}</span>
            </div>
            {region === r.value && (
              <span className="ml-auto text-gold">✓</span>
            )}
          </DropdownMenuItem>
        ))}
        
        <DropdownMenuSeparator className="bg-cream/10" />
        
        <DropdownMenuLabel className="text-cream/50 text-xs uppercase tracking-wider px-4 py-2">
          Currency
        </DropdownMenuLabel>
        {currencies.map((c) => (
          <DropdownMenuItem
            key={c.value}
            onClick={() => setCurrency(c.value)}
            className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
              currency === c.value 
                ? "bg-gold/10 text-gold" 
                : "text-cream/80 hover:bg-cream/5 hover:text-cream"
            }`}
          >
            <span className="font-body text-sm font-medium w-10">{c.symbol}</span>
            <span className="font-body text-sm">{c.label}</span>
            {currency === c.value && (
              <span className="ml-auto text-gold">✓</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
